import { useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../lib/api";

/**
 * /reset-password?token=xxx — user picks a new password.
 * Successful reset invalidates their old JWT server-side; we clear local
 * storage and send them to sign in again.
 */
export default function ResetPassword() {
  const { search } = useLocation();
  const navigate = useNavigate();
  const token = useMemo(() => new URLSearchParams(search).get("token") || "", [search]);

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const clientStrongEnough = (pw) => pw.length >= 10 && /[A-Za-z]/.test(pw) && /\d/.test(pw);

  const submit = async (e) => {
    e.preventDefault();
    if (!token) {
      toast.error("Missing reset token.");
      return;
    }
    if (password !== confirm) {
      toast.error("Passwords do not match.");
      return;
    }
    if (!clientStrongEnough(password)) {
      toast.error("Password must be 10+ chars and include a letter and a number.");
      return;
    }
    setBusy(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: password });
      // Any existing JWT for this user is now invalid — clear our client-side session.
      try {
        window.localStorage.removeItem("brep_token");
        window.localStorage.removeItem("brep_user");
      } catch (_) { /* noop */ }
      setDone(true);
      setTimeout(() => navigate("/"), 3500);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Reset failed. Request a new link.");
    } finally {
      setBusy(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-[#050510] text-white flex items-center justify-center p-6" data-testid="reset-no-token">
        <div className="max-w-md w-full">
          <div className="text-[11px] tracking-[0.32em] uppercase text-[#2A5BFF]">Password</div>
          <h1 className="font-anton uppercase text-3xl mt-4">Missing token</h1>
          <p className="mt-4 text-white/70 text-sm">
            Open the reset link from your email. If it's expired, request a new one.
          </p>
          <Link to="/forgot-password" className="btn-primary inline-block mt-8 px-6 py-3 text-[11px] tracking-[0.24em] uppercase">
            Request new link
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050510] text-white flex items-center justify-center p-6" data-testid="reset-password-page">
      <div className="w-full max-w-md">
        <div className="text-[11px] tracking-[0.32em] uppercase text-[#2A5BFF]">Password</div>
        <h1 className="font-anton uppercase text-4xl md:text-5xl mt-4 leading-[0.95]">Set a new password</h1>

        {!done ? (
          <form onSubmit={submit} className="mt-10">
            <label className="text-[10px] tracking-[0.32em] uppercase text-white/50">New password</label>
            <input
              type="password"
              autoComplete="new-password"
              minLength={10}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className="input-line focus-ring"
              data-testid="reset-new-password"
            />
            <p className="mt-2 text-[11px] text-white/40">Minimum 10 characters, must include a letter and a number.</p>

            <label className="text-[10px] tracking-[0.32em] uppercase text-white/50 mt-6 block">Confirm password</label>
            <input
              type="password"
              autoComplete="new-password"
              minLength={10}
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              required
              className="input-line focus-ring"
              data-testid="reset-confirm-password"
            />

            <button
              type="submit"
              disabled={busy}
              className="btn-primary mt-8 w-full px-6 py-4 text-[12px] tracking-[0.22em] uppercase font-medium disabled:opacity-60"
              data-testid="reset-submit"
            >
              {busy ? "…" : "Update password"}
            </button>
          </form>
        ) : (
          <div className="mt-10 border border-[#2A5BFF]/40 bg-[#2A5BFF]/[0.06] p-6" data-testid="reset-done">
            <div className="text-[10px] tracking-[0.32em] uppercase text-[#2A5BFF]">Password updated</div>
            <p className="mt-3 text-white/85 text-sm leading-relaxed">
              You've been signed out on every device. Redirecting home — sign in with your new password.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
